import * as _ from 'lodash';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Item, DropdownTreeMenu, SelectableItem } from 'fc-ui-components';

import { LanguageService } from 'services/language.service';
import { SeasonName, Season, sortBy } from 'helpers';
import { Facet, FacetWithName } from './searcher.types';
import {
    activeStatusTranslationKeyMap,
    orderAreaTargetGroupTranslationKeyMap,
    targetGroupTranslationKeyMap,
    categoryTranslationKeyMap,
    colorTranslationKeyMap
} from './constants';
import { seasonTranslationKeyMap } from 'helpers';
import { FilterSelectableItem } from 'services/product-search/filters-provider';
import { FacetOptions, FilterOptions } from 'app/b2b-order/retailer-platform/order/types';
import { sortSizes } from 'app/b2b-order/helpers/size';
import { ClientType, OrderType } from 'app/order-management/components/order-history/order-history.component';

export class FacetsUiMapper {
    constructor(private languageService: LanguageService) {}

    public mapFacetToSeasonsFilter(
        // Facet key format: fall_winter2017
        seasonsFacet: Facet[] | undefined,
        // Season format: fall_winter2017 or fall_winter|2017
        selectedSeasons: string[]
    ): Observable<FilterSelectableItem[]> {
        const selectedSeasonsWithoutPipe = this.removeSeasonsPipes(selectedSeasons);
        const seasonsFilter = this.mergeFacetsAndSelectedItems(seasonsFacet, selectedSeasonsWithoutPipe);

        const seasons = _.chain(seasonsFilter)
            .map(({ key }) => this.mapSeasonKeyToSeason(key))
            .filter((season: Season) => !!seasonTranslationKeyMap[season.name])
            .orderBy(['year', 'name'], ['desc', 'asc'])
            .value();

        if (_.isEmpty(seasons)) {
            return of([]);
        }

        const seasonsWithNosAtFrontIfPresent = [
            ..._.filter(seasons, ({ name }) => name === SeasonName.NOS),
            ..._.filter(seasons, ({ name }) => name !== SeasonName.NOS)
        ];

        const filterItems: Observable<FilterSelectableItem>[] = _.map(
            seasonsWithNosAtFrontIfPresent,
            (season: Season) =>
                this.mapSeasonToItem(season).pipe(
                    map((filterItem) => ({
                        ...filterItem,
                        text: `${filterItem.text}`,
                        label: `${filterItem.text}`,
                        checked: _.includes(selectedSeasons, filterItem.value)
                    }))
                )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToSubSeasonsFilter(
        subSeasonsFacet: Facet[] | undefined,
        selectedSubSeasons: string[]
    ): Observable<FilterSelectableItem[]> {
        const subSeasonsFilter = this.mergeFacetsAndSelectedItems(subSeasonsFacet, selectedSubSeasons);
        const subSeasons = _.orderBy(subSeasonsFilter, 'key', 'asc');

        if (_.isEmpty(subSeasons)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(subSeasons, ({ key }) =>
            this.mapSubSeasonKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedSubSeasons, filterItem.value)
                }))
            )
        );

        return forkJoin(filterItems);
    }

    public mapFacetToTargetGroupsFilter(
        targetGroupsFacet: Facet[] | undefined,
        selectedTargetGroups: string[]
    ): Observable<FilterSelectableItem[]> {
        const targetGroupFilter = this.mergeFacetsAndSelectedItems(targetGroupsFacet, selectedTargetGroups);
        const targetGroups = _.orderBy(targetGroupFilter, 'key', 'asc');

        if (_.isEmpty(targetGroups)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(targetGroups, ({ key }) =>
            this.mapTargetGroupKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedTargetGroups, filterItem.value)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToCategoriesFilter(
        categoriesFacet: Facet[] | undefined,
        selectedCategories: string[]
    ): Observable<FilterSelectableItem[]> {
        const categoriesFilter = this.mergeFacetsAndSelectedItems(categoriesFacet, selectedCategories);
        const categories = _.orderBy(categoriesFilter, 'key', 'asc');

        if (_.isEmpty(categories)) {
            return of([]);
        }

        const translatableCategories = _.filter(categories, (category) => !!categoryTranslationKeyMap[category.key]);

        const filterItems: Observable<FilterSelectableItem>[] = _.map(translatableCategories, ({ key }) =>
            this.mapCategoryKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedCategories, filterItem.value)
                }))
            )
        );

        return forkJoin(filterItems);
    }

    public mapFacetToColorsFilter(
        colorsFacet: Facet[] | undefined,
        selectedColors: string[]
    ): Observable<FilterSelectableItem[]> {
        const colorsFilter = this.mergeFacetsAndSelectedItems(colorsFacet, selectedColors);
        const colors = _.orderBy(colorsFilter, 'key', 'asc');

        if (_.isEmpty(colors)) {
            return of([]);
        }

        const translatableColors = _.filter(colors, (color) => !!colorTranslationKeyMap[color.key]);

        const filterItems: Observable<FilterSelectableItem>[] = _.map(translatableColors, ({ key }) =>
            this.mapColorKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedColors, filterItem.value)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public mapActiveStatusesToFilter(selectedActiveStatuses: string[]): Observable<FilterSelectableItem[]> {
        const activeStatusKeys: string[] = Object.keys(activeStatusTranslationKeyMap);

        const filterItems: Observable<FilterSelectableItem>[] = _.map(activeStatusKeys, (key) =>
            this.mapActiveStatusKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedActiveStatuses, filterItem.value)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToOrderAreaTargetGroupsFilter(
        targetGroupsFacet: Facet[] | undefined,
        selectedTargetGroups: string[]
    ): Observable<FilterSelectableItem[]> {
        const targetGroupFilter = this.mergeFacetsAndSelectedItems(targetGroupsFacet, selectedTargetGroups);

        const targetGroupSortOrder = ['womenswear', 'menswear', 'unisex', 'teens', 'kids', 'babies', 'newborn'];
        const targetGroups = _.sortBy(targetGroupFilter, (targetGroup) =>
            targetGroupSortOrder.indexOf(targetGroup.key)
        );

        if (_.isEmpty(targetGroups)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(
            targetGroups.filter((targetGroup) => !!orderAreaTargetGroupTranslationKeyMap[targetGroup.key]),
            ({ key }) =>
                this.mapOrderAreaTargetGroupKeyToItem(key).pipe(
                    map((filterItem) => ({
                        ...filterItem,
                        text: `${filterItem.text}`,
                        label: `${filterItem.text}`,
                        checked: _.includes(selectedTargetGroups, filterItem.value)
                    }))
                )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToFitsFilter(
        fitsFacet: Facet[] | undefined,
        selectedFits: string[]
    ): Observable<FilterSelectableItem[]> {
        const fitFilter = this.mergeFacetsAndSelectedItems(fitsFacet, selectedFits);
        const fits = _.orderBy(fitFilter, 'key', 'asc');

        if (_.isEmpty(fits)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(fits, ({ key }) =>
            this.mapFitKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedFits, filterItem.value)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToCatalogsFilter(
        catalogsFacet: FacetWithName[] | undefined,
        selectedCatalogs: string[]
    ): Observable<FilterSelectableItem[]> {
        const catalogFilter = this.mergeFacetsAndSelectedItems(catalogsFacet, selectedCatalogs);
        const catalogs = _.orderBy(catalogFilter, 'name', 'asc');

        if (_.isEmpty(catalogs)) {
            return of([]);
        }

        const catalogsWithNames = catalogs.map((catalog) => ({
            ...catalog,
            name: catalogsFacet.find((facet) => facet.key === catalog.key)?.name || catalog.key
        }));

        const filterItems: Observable<FilterSelectableItem>[] = _.map(catalogsWithNames, ({ key, name }) =>
            this.mapFitKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${name}`,
                    label: `${name}`,
                    checked: _.includes(selectedCatalogs, filterItem.value)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public mapFacetToSizesFilter(
        sizeFacets: Facet[] | undefined,
        selectedSizes: string[]
    ): Observable<FilterSelectableItem[]> {
        const sizeFilter = this.mergeFacetsAndSelectedItems(sizeFacets, selectedSizes);
        const sizes = this.sortSizeFilter(sizeFilter).map((size) => ({ key: size.size, ...size }));
        if (_.isEmpty(sizes)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(sizes, ({ key }) =>
            this.mapSizeKeyToItem(key).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedSizes, filterItem.value)
                }))
            )
        );

        return forkJoin(filterItems);
    }

    public mapFacetToDetailedSizesFilter(
        sizeFacets: Partial<FacetOptions> | undefined,
        selectedSizes: FilterOptions
    ): Observable<DropdownTreeMenu> {
        const mapPrefix = {
            widths: 'W',
            lengths: 'L'
        };

        return this.languageService
            .translateObservable([
                'ORDER.PRODUCT_SEARCH.DETAILED_SIZES.GENERAL_SIZES',
                'ORDER.PRODUCT_SEARCH.DETAILED_SIZES.WIDTHS',
                'ORDER.PRODUCT_SEARCH.DETAILED_SIZES.LENGTHS'
            ])
            .pipe(
                map((translationMap) => ({
                    sections: Object.values(DetailedSizes)
                        .map((detailedSize) => {
                            const facets = this.mergeFacetsAndSelectedItems(
                                sizeFacets[detailedSize],
                                selectedSizes[detailedSize]
                            );

                            const sortDetailedSizes = (facetsToSort) =>
                                detailedSize === DetailedSizes.GeneralSizes
                                    ? this.sortSizeFilter(facetsToSort).map(({ size }) => ({ key: size }))
                                    : this.sortDetailedSizeFilter(facetsToSort);

                            return {
                                isOpen: true,
                                label: translationMap[
                                    `ORDER.PRODUCT_SEARCH.DETAILED_SIZES.${_.snakeCase(
                                        detailedSize
                                    ).toLocaleUpperCase()}`
                                ],
                                items: sortDetailedSizes(_.isEmpty(facets) ? [] : facets).map(({ key }) => ({
                                    label: `${_.isEmpty(mapPrefix[detailedSize]) ? '' : mapPrefix[detailedSize]}${key}`,
                                    value: `${detailedSize}:${key}`
                                }))
                            };
                        })
                        .filter(({ items }) => items.length > 0),
                    isCollapsable: true
                }))
            );
    }

    public mapFacetToDeliveryDatesFilter(
        deliveryDatesFacets: Facet[] | undefined,
        selectedDeliveryDates: string[]
    ): Observable<SelectableItem[]> {
        const deliveryDates = this.buildDeliveryDatesFromFacetsAndSelectedItems(
            deliveryDatesFacets,
            selectedDeliveryDates
        );
        if (_.isEmpty(deliveryDates)) {
            return of([]);
        }

        const filterItems: Observable<SelectableItem>[] = _.map(deliveryDates, ({ key }) =>
            this.mapKeyToItem(key).pipe(
                map((filterItem) => {
                    const [startDate, endDate] = filterItem.text.split('-');
                    return {
                        ...filterItem,
                        ...(endDate ? { text: `${startDate} - ${endDate}` } : { text: filterItem.text }),
                        ...(endDate ? { label: `${startDate} - ${endDate}` } : { label: filterItem.text }),
                        checked: _.includes(selectedDeliveryDates, filterItem.value)
                    };
                })
            )
        );

        return forkJoin(filterItems);
    }

    public mapFacetToBrandsAndRetailersFilter(
        orderHistoryFacet: OrderHistoryFacet[] | undefined,
        selectedOrderTypes: string[]
    ): Observable<FilterSelectableItem[]> {
        const orderTypeFilter = this.mergeBrandRetailerFacetsAndSelectedItems(orderHistoryFacet, selectedOrderTypes);
        const orderTypes = sortBy(orderTypeFilter, 'name[0]');

        if (_.isEmpty(orderTypes)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(orderTypes, ({ name, _id }) =>
            this.mapKeyToItem(name[0], _id).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: filterItem.text,
                    label: filterItem.text,
                    value: _id,
                    checked: _.includes(selectedOrderTypes, _id)
                }))
            )
        );

        return forkJoin(filterItems);
    }

    public mapFacetToBrandsAndRetailersFilterV2(
        orderHistoryFacet: OrderHistoryFacet[] | undefined,
        selectedOrderTypes: string[]
    ): Observable<DropdownTreeMenu> {
        const orderTypeFilter = this.mergeBrandRetailerFacetsAndSelectedItems(orderHistoryFacet, selectedOrderTypes);
        const orderTypes = sortBy(orderTypeFilter, 'name[0]');

        if (_.isEmpty(orderTypes)) {
            return of();
        }

        const filterItems: Observable<DropdownTreeMenu> = of({
            isCollapsable: false,
            sections: [
                {
                    items: _.map(orderTypes, ({ name, _id }) => ({
                        label: this.mapKeyToItemV2(name[0], _id).text,
                        value: this.mapKeyToItemV2(name[0], _id).value
                    })),
                    label: '',
                    isOpen: true
                }
            ]
        });

        return filterItems;
    }

    public mapFacetToOrderTypesFilter(
        orderTypeFacet: OrderTypeFacet[] | undefined,
        selectedOrderTypes: string[]
    ): Observable<FilterSelectableItem[]> {
        const orderTypeFilter = this.mergeOrderTypeFacetsAndSelectedItems(orderTypeFacet, selectedOrderTypes);
        const orderTypes = _.orderBy(orderTypeFilter, 'key', 'asc');

        if (_.isEmpty(orderTypes)) {
            return of([]);
        }

        const filterItems: Observable<FilterSelectableItem>[] = _.map(orderTypes, ({ _id }) =>
            this.mapOrderAreaOrderTypeKeyToItem(this.getOrderTypeName(_id as ClientType)).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: filterItem.text,
                    label: filterItem.text,
                    value: _id,
                    checked: _.includes(selectedOrderTypes, _id)
                }))
            )
        );
        return forkJoin(filterItems);
    }

    public getOrderTypeName(clientType: ClientType) {
        const typeNameMap = {
            [ClientType.FcPlatform]: OrderType.Reorder,
            [ClientType.Clara]: OrderType.Clara,
            [ClientType.SmartReplenishment]: OrderType.SmartReplenishment,
            [ClientType.AutoRestock]: OrderType.AutoRestock,
            [ClientType.Suggestions]: OrderType.Suggestions,
            [ClientType.FcPlatformPreorder]: OrderType.Preorder,
            [ClientType.Showroom]: OrderType.Showroom
        };
        return typeNameMap[clientType] || OrderType.NonPlatformOrder;
    }

    public mapFacetToOrderDatesFilter(
        orderDateFacet: OrderHistoryFacet[] | undefined,
        selectedOrderDate: string
    ): Observable<FilterSelectableItem[]> {
        const orderDateFilter = this.mergeOrderHistoryFacetsAndSelectedItems(orderDateFacet, selectedOrderDate);
        const orderDates = _.orderBy(orderDateFilter, 'key', 'asc');
        if (_.isEmpty(orderDates)) {
            return of([]);
        }
        const filterItems: Observable<FilterSelectableItem>[] = _.map(orderDates, ({ _id, name }) => {
            const normalizedName = Array.isArray(name) ? name[0] : name;
            return this.mapKeyToItem(normalizedName, _id).pipe(
                map((filterItem) => ({
                    ...filterItem,
                    text: `${filterItem.text}`,
                    label: `${filterItem.text}`,
                    checked: _.includes(selectedOrderDate, filterItem.value)
                }))
            );
        });
        return forkJoin(filterItems);
    }

    public mapSearchParamsToSeasonLabels(seasonSearchParams: string[]): Observable<Item[]> {
        const validSeasons = _.chain(seasonSearchParams)
            .map((key) => this.mapSeasonKeyToSeason(_.replace(key, '|', '')))
            .filter((season: Season) => !!seasonTranslationKeyMap[season.name])
            .orderBy(['year', 'name'], ['desc', 'asc'])
            .value();

        if (_.isEmpty(validSeasons)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validSeasons, (season: Season) => this.mapSeasonToItem(season));

        return forkJoin(labelItems);
    }

    public mapSearchParamsToTargetGroupLabels(targetGroupSearchParams: string[]): Observable<Item[]> {
        const validTargetGroups = _.chain(targetGroupSearchParams)
            .filter((targetGroup) => !!targetGroupTranslationKeyMap[targetGroup])
            .orderBy('key', 'asc')
            .value();

        if (_.isEmpty(validTargetGroups)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validTargetGroups, (targetGroup) =>
            this.mapTargetGroupKeyToItem(targetGroup)
        );

        return forkJoin(labelItems);
    }

    public mapSearchParamsToCategoryLabel(categorySearchParam: string): Observable<Item> {
        if (!categorySearchParam) {
            return of(null);
        }
        return this.mapCategoryKeyToItem(categorySearchParam);
    }

    public mapSearchParamsToCategoriesLabel(categoriesSearchParam: string[]): Observable<Item[]> {
        const validCategories = _.chain(categoriesSearchParam)
            .filter((category) => !!categoryTranslationKeyMap[category])
            .orderBy('key', 'asc')
            .value();

        if (_.isEmpty(validCategories)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validCategories, (category) =>
            this.mapCategoryKeyToItem(category)
        );

        return forkJoin(labelItems);
    }

    public mapSearchParamsToColorLabels(colorSearchParams: string[]): Observable<Item[]> {
        const validColors = _.chain(colorSearchParams)
            .filter((color) => !!colorTranslationKeyMap[color])
            .orderBy('key', 'asc')
            .value();

        if (_.isEmpty(validColors)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validColors, (color) => this.mapColorKeyToItem(color));

        return forkJoin(labelItems);
    }

    public mapSearchParamsToOATargetGroupLabels(targetGroupSearchParams: string[]): Observable<Item[]> {
        const validTargetGroups = _.chain(targetGroupSearchParams)
            .filter((targetGroup) => !!orderAreaTargetGroupTranslationKeyMap[targetGroup])
            .orderBy('key', 'asc')
            .value();

        if (_.isEmpty(validTargetGroups)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validTargetGroups, (targetGroup) =>
            this.mapOrderAreaTargetGroupKeyToItem(targetGroup)
        );

        return forkJoin(labelItems);
    }

    public mapSearchParamsToSizeLabels(sizeSearchParams: string[]): Observable<Item[]> {
        const validSizes = _.chain(sizeSearchParams).orderBy('key', 'asc').value();

        if (_.isEmpty(validSizes)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validSizes, (size) => this.mapSizeKeyToItem(size));

        return forkJoin(labelItems);
    }

    public mapSearchParamsToFitLabels(fitSearchParams: string[]): Observable<Item[]> {
        const validFits = _.chain(fitSearchParams).orderBy('key', 'asc').value();

        if (_.isEmpty(validFits)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validFits, (fit) => this.mapFitKeyToItem(fit));

        return forkJoin(labelItems);
    }

    public mapSearchParamsToActiveStatusLabels(activeStatusSearchParams: string[]): Observable<Item[]> {
        const validStatuses = _.chain(activeStatusSearchParams)
            .filter((status) => !!activeStatusTranslationKeyMap[status])
            .orderBy('key', 'asc')
            .value();

        if (_.isEmpty(validStatuses)) {
            return of([]);
        }
        const labelItems: Observable<Item>[] = _.map(validStatuses, (status) => this.mapActiveStatusKeyToItem(status));

        return forkJoin(labelItems);
    }

    public mapSearchParamsToAwaitingInitialDeliveryLabels(
        awaitingInitialDelivery: boolean | undefined
    ): Observable<Item[]> {
        if (!awaitingInitialDelivery) {
            return of([]);
        }

        return this.languageService
            .translateObservable('SMART_REPLENISHMENT_AUTO_RESTOCK.INITIAL_DELIVERY_STATUS_FILTER.TITLE')
            .pipe(
                map((label) => [
                    {
                        text: label.toString(),
                        value: 'awaitingInitialDelivery'
                    }
                ])
            );
    }

    private mapOrderAreaTargetGroupKeyToItem(targetGroupKey: string): Observable<Item> {
        return this.languageService.translateObservable(orderAreaTargetGroupTranslationKeyMap[targetGroupKey]).pipe(
            map((targetGroupName) => ({
                text: targetGroupName.toString(),
                value: targetGroupKey
            }))
        );
    }

    private mapOrderAreaOrderTypeKeyToItem(orderTypeKey: string): Observable<Item> {
        return this.languageService.translateObservable(orderTypeKey).pipe(
            map((orderTypeName) => ({
                text: orderTypeName.toString(),
                value: orderTypeKey
            }))
        );
    }

    private mapTargetGroupKeyToItem(targetGroupKey: string): Observable<Item> {
        return this.languageService.translateObservable(targetGroupTranslationKeyMap[targetGroupKey]).pipe(
            map((targetGroupName) => ({
                text: targetGroupName.toString(),
                value: targetGroupKey
            }))
        );
    }

    private mapSubSeasonKeyToItem(subSeasonKey: string): Observable<Item> {
        return this.languageService.translateObservable(subSeasonKey).pipe(
            map((subSeasonName) => ({
                text: subSeasonName.toString(),
                value: subSeasonKey
            }))
        );
    }

    private mapCategoryKeyToItem(categoryKey: string): Observable<Item> {
        return this.languageService.translateObservable(categoryTranslationKeyMap[categoryKey]).pipe(
            map((categoryName) => ({
                text: categoryName.toString(),
                value: categoryKey
            }))
        );
    }

    private mapColorKeyToItem(colorKey: string): Observable<Item> {
        return this.languageService
            .translateObservable(colorTranslationKeyMap[colorKey])
            .pipe(map((colorName) => ({ text: colorName.toString(), value: colorKey })));
    }

    private mapActiveStatusKeyToItem(activeStatusKey: string): Observable<Item> {
        return this.languageService.translateObservable(activeStatusTranslationKeyMap[activeStatusKey]).pipe(
            map((activeStatusName) => ({
                text: activeStatusName.toString(),
                value: activeStatusKey
            }))
        );
    }

    private mapFitKeyToItem(fitKey: string): Observable<Item> {
        return of({ text: fitKey, value: fitKey });
    }

    private mapSizeKeyToItem(sizeKey: string): Observable<Item> {
        return of({
            text: sizeKey.toString(),
            value: sizeKey
        });
    }

    private mapKeyToItem(key: string, id?: string): Observable<Item> {
        return of({
            text: key.toString(),
            value: id ? id : key
        });
    }

    private mapKeyToItemV2(key: string, id?: string): Item {
        return {
            text: key.toString(),
            value: id ? id : key
        };
    }

    private mapSeasonToItem(season: Season): Observable<Item> {
        const value = `${season.name}${season.year ? '|' : ''}${season.year}`;

        return this.languageService.translateObservable(seasonTranslationKeyMap[season.name]).pipe(
            map((seasonName) => ({
                text: `${seasonName} ${season.year}`,
                value
            }))
        );
    }

    private mapSeasonKeyToSeason(seasonKey: string) {
        const isNOSSeason = _.startsWith(seasonKey, SeasonName.NOS);
        return isNOSSeason
            ? { name: SeasonName.NOS, year: '' }
            : {
                  name: seasonKey.slice(0, seasonKey.length - 4),
                  year: seasonKey.slice(-4)
              };
    }

    private mapSelectedKeysToFacetKeys(keys: string[]): Facet[] {
        return _.map(keys, (key) => ({ key, count: 0 }));
    }

    private mapSelectedKeysToOrderHistoryFacetKeys(keys: string[]): OrderHistoryFacet[] {
        return _.map(keys, (key) => ({ name: [key], _id: key }));
    }

    private mapSelectedKeysToBrandRetailerFacetKeys(facets: OrderHistoryFacet[], keys: string[]): OrderHistoryFacet[] {
        return _.map(keys, (key) => ({ name: _.find(facets, (f) => f._id === key).name, _id: key }));
    }

    private mapSelectedKeysToOrderTypeFacetKeys(keys: string[]): OrderTypeFacet[] {
        return _.map(keys, (key) => ({ _id: key }));
    }

    private removeSeasonsPipes(seasons: string[]): string[] {
        return _.map(seasons, (season) => season.replace('|', ''));
    }

    private mergeFacetsAndSelectedItems(facets: Facet[], selectedItems: string[]): Facet[] {
        const filter = !_.isEmpty(selectedItems) ? this.mapSelectedKeysToFacetKeys(selectedItems) : [];
        if (facets) {
            const unselectedFacets = _.filter(facets, (facet) => !_.includes(selectedItems, facet.key));
            return [...filter, ...unselectedFacets];
        }
        return filter;
    }

    private mergeOrderHistoryFacetsAndSelectedItems(
        facets: OrderHistoryFacet[],
        selectedItems: string[] | string
    ): OrderHistoryFacet[] {
        const wrappedSelectedItems = Array.isArray(selectedItems) ? selectedItems : [selectedItems];

        const filter = !_.isEmpty(selectedItems)
            ? this.mapSelectedKeysToOrderHistoryFacetKeys(wrappedSelectedItems)
            : [];
        if (facets) {
            const unselectedFacets = _.filter(facets, (facet) => !_.includes(selectedItems, facet._id));
            return [...filter, ...unselectedFacets];
        }
        return filter;
    }

    private mergeOrderTypeFacetsAndSelectedItems(facets: OrderTypeFacet[], selectedItems: string[]): OrderTypeFacet[] {
        const filter = !_.isEmpty(selectedItems) ? this.mapSelectedKeysToOrderTypeFacetKeys(selectedItems) : [];
        if (facets) {
            const unselectedFacets = _.filter(facets, (facet) => !_.includes(selectedItems, facet._id));
            return [...filter, ...unselectedFacets];
        }
        return filter;
    }

    private mergeBrandRetailerFacetsAndSelectedItems(
        facets: OrderHistoryFacet[],
        selectedItems: string[]
    ): OrderHistoryFacet[] {
        const filter = !_.isEmpty(selectedItems)
            ? this.mapSelectedKeysToBrandRetailerFacetKeys(facets, selectedItems)
            : [];
        if (facets) {
            const unselectedFacets = _.filter(facets, (facet) => !_.includes(selectedItems, facet._id));
            return [...filter, ...unselectedFacets];
        }
        return filter;
    }

    private buildDeliveryDatesFromFacetsAndSelectedItems(facets: Facet[], selectedItems: string[]): Facet[] {
        const deliveryDatesFacets = this.mergeFacetsAndSelectedItems(facets, selectedItems);
        const dates = _.map(deliveryDatesFacets, this.addStartAndEndDatesToDeliveryDateFacet);
        const datesSortedByStartDate = _.sortBy(dates, ['startDate']);
        return datesSortedByStartDate.map((deliveryDate) => _.omit(deliveryDate, ['startDate', 'endDate']));
    }

    private addStartAndEndDatesToDeliveryDateFacet({ key, count }: Facet) {
        const splitDates = key.split('-');
        const startDate = moment(splitDates[0], 'DD.MM.YYYY');
        const endDate = splitDates.length > 1 ? moment(splitDates[1], 'DD.MM.YYYY') : startDate;
        return { key, count, startDate, endDate };
    }

    private sortSizeFilter(sizeFilter: Facet[]) {
        const sizes = sizeFilter.map((filter) => ({ size: filter.key, count: filter.count }));
        return sortSizes(sizes, false);
    }

    private sortDetailedSizeFilter(keys: Facet[]) {
        return keys.sort(({ key: firstKey }, { key: secondKey }) => {
            const replaceComma = (s) => s.replace(/,/g, '.');
            const normalizedFirstKey = replaceComma(firstKey);
            const normalizedSecondKey = replaceComma(secondKey);
            const isFirstKeyNaN = Number.isNaN(Number(normalizedFirstKey));
            const isSecondKeyNaN = Number.isNaN(Number(normalizedSecondKey));
            if (isFirstKeyNaN && isSecondKeyNaN) {
                return normalizedFirstKey.localeCompare(normalizedSecondKey);
            }
            if (isFirstKeyNaN && !isSecondKeyNaN) {
                return 1;
            }
            if (!isFirstKeyNaN && isSecondKeyNaN) {
                return -1;
            }
            return Number(normalizedFirstKey) < Number(normalizedSecondKey) ? -1 : 1;
        });
    }
}

export interface OrderHistoryFacet {
    _id: string;
    name: string[];
}

export interface OrderTypeFacet {
    _id: string;
}

export interface OrderHistoryFacets {
    brands: OrderHistoryFacet[];
    retailers: OrderHistoryFacet[];
    orderTypes: OrderHistoryFacet[];
    count: { total: number };
}

export enum DetailedSizes {
    GeneralSizes = 'generalSizes',
    Widths = 'widths',
    Lengths = 'lengths'
}
